import { X } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

/**
 * The document tag look: the Issues page's outline discipline pill (white,
 * grey border — `DisciplineBadge` variant="outline"), for categories,
 * disciplines and stages alike.
 */
export const DOCUMENT_TAG_PILL_CLASS =
  "inline-flex max-w-[150px] items-center rounded-full border border-border px-2 py-0.5 text-[11px] font-medium leading-4 text-[#262626]";

/** Categories white, disciplines light grey, stages a shade darker. */
export type DocumentTagTone = "category" | "discipline" | "stage";

const TONE_CLASS: Record<DocumentTagTone, { pill: string; fade: string }> = {
  category: { pill: "bg-white", fade: "from-white" },
  discipline: { pill: "bg-[#f4f4f5]", fade: "from-[#f4f4f5]" },
  stage: { pill: "bg-[#e7e7e9]", fade: "from-[#e7e7e9]" },
};

export function documentTagToneClass(tone: DocumentTagTone): string {
  return TONE_CLASS[tone].pill;
}

interface DocumentTagPillProps {
  label: string;
  tone: DocumentTagTone;
  /** Shows a remove button on hover/focus, drawn INSIDE the pill. */
  onRemove?: () => void;
  removeLabel?: string;
  onClick?: () => void;
  className?: string;
}

/**
 * One document tag. The remove button overlays the pill's right edge, over a
 * white fade, instead of being inserted after the label — so hovering never
 * widens the pill and the row around it never reflows.
 */
export function DocumentTagPill({
  label,
  tone,
  onRemove,
  removeLabel,
  onClick,
  className,
}: DocumentTagPillProps) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={cn(
              DOCUMENT_TAG_PILL_CLASS,
              TONE_CLASS[tone].pill,
              "group/tag relative",
              onClick && "cursor-pointer",
              className
            )}
            onClick={onClick}
          >
            <span className="truncate">{label}</span>
            {onRemove && (
              <button
                type="button"
                className={cn(
                  "absolute inset-y-0 right-0 flex items-center rounded-r-full pl-3 pr-1",
                  "bg-gradient-to-l from-60% to-transparent",
                  TONE_CLASS[tone].fade,
                  "pointer-events-none opacity-0 transition-opacity",
                  "group-hover/tag:pointer-events-auto group-hover/tag:opacity-100",
                  "focus-visible:pointer-events-auto focus-visible:opacity-100 focus-visible:outline-none"
                )}
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove();
                }}
                aria-label={removeLabel ?? `Remove ${label}`}
              >
                <X className="h-3 w-3 rounded-full text-muted-foreground hover:text-foreground" />
              </button>
            )}
          </span>
        </TooltipTrigger>
        <TooltipContent side="top">
          <p className="text-xs">{label}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
